import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Tab, Nav, Table, Button, Form, Modal, Badge, ListGroup } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './ProviderDashboard.css';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

moment.locale('fr');
const localizer = momentLocalizer(moment);

const ProviderDashboard = () => {
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [activeChats, setActiveChats] = useState([]);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Service Modal
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServicePrice, setNewServicePrice] = useState('');
  const [newServiceDuration, setNewServiceDuration] = useState(30);
  const [newServiceDesc, setNewServiceDesc] = useState('');
  const [newServiceMaxCapacity, setNewServiceMaxCapacity] = useState(1);

  // Edit Service Modal States
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [editTargetService, setEditTargetService] = useState(null);
  const [editServiceName, setEditServiceName] = useState('');
  const [editServicePrice, setEditServicePrice] = useState('');
  const [editServiceDuration, setEditServiceDuration] = useState(30);
  const [editServiceDesc, setEditServiceDesc] = useState('');
  const [editServiceMaxCapacity, setEditServiceMaxCapacity] = useState(1);

  // Service Promotion Modal
  const [showPromoServiceModal, setShowPromoServiceModal] = useState(false);
  const [promoTargetService, setPromoTargetService] = useState(null);
  const [promoServicePrice, setPromoServicePrice] = useState('');
  const [promoServiceLabel, setPromoServiceLabel] = useState('');

  // Promotion Modal
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoType, setPromoType] = useState('percentage');
  const [promoValue, setPromoValue] = useState('');

  // Walk-in Booking Modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [walkinClientName, setWalkinClientName] = useState('');
  const [walkinClientPhone, setWalkinClientPhone] = useState('');
  const [walkinClientEmail, setWalkinClientEmail] = useState('');
  const [walkinServiceId, setWalkinServiceId] = useState('');
  const [walkinDate, setWalkinDate] = useState('');
  const [walkinTime, setWalkinTime] = useState('09:00');
  const [walkinNotes, setWalkinNotes] = useState('');
  const [walkinGuestsCount, setWalkinGuestsCount] = useState(1);
  const [walkinSelectedResourceName, setWalkinSelectedResourceName] = useState('');

  // Boutique Config
  const [businessName, setBusinessName] = useState('');
  const [businessDesc, setBusinessDesc] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [logo, setLogo] = useState('');
  
  // PIN Security States
  const [managerPin, setManagerPin] = useState('1111');
  const [cashiers, setCashiers] = useState([]);
  const [sessionRole, setSessionRole] = useState(null); // null, 'manager', 'cashier'
  const [sessionCashierName, setSessionCashierName] = useState('');
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [resourceName, setResourceName] = useState('Table');
  const [resourcesList, setResourcesList] = useState([]);

  const token = localStorage.getItem('token');
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

  useEffect(() => {
    if (!token || !user?._id) {
      navigate('/Login');
      return;
    }
    // Initialize config inputs
    setBusinessName(user.businessName || '');
    setBusinessDesc(user.description || '');
    setBusinessAddress(user.adress || '');
    setBusinessPhone(user.phone?.toString() || '');
    setManagerPin(user.managerPin || '1111');
    setCashiers(user.cashiers || [{ name: 'Caissier Principal', pin: '2222' }]);
    setResourceName(user.resourceName || 'Table');
    setResourcesList(user.resources || []);
    setLogo(user.logo || '');

    loadProviderData();
  }, []);

  useEffect(() => {
    if (showBookingModal && user) {
      const providerActName = user.activity && typeof user.activity === 'object' 
        ? user.activity.name 
        : user.activity;
      let providerResources = resourcesList || [];
      if (providerResources.length === 0) {
        if (providerActName?.toLowerCase().includes('terrain')) {
          providerResources = [
            { name: 'Court A', capacity: 10 },
            { name: 'Court B', capacity: 10 },
            { name: 'Court C', capacity: 10 }
          ];
        } else {
          providerResources = [
            { name: 'Table 1', capacity: 2 },
            { name: 'Table 2', capacity: 2 },
            { name: 'Table 3', capacity: 4 },
            { name: 'Table 4', capacity: 4 },
            { name: 'Table 5', capacity: 6 }
          ];
        }
      }
      const matches = providerResources.filter(r => r.capacity >= walkinGuestsCount);
      if (matches.length > 0) {
        if (!walkinSelectedResourceName || !matches.some(m => m.name === walkinSelectedResourceName)) {
          setWalkinSelectedResourceName(matches[0].name);
        }
      } else {
        setWalkinSelectedResourceName('');
      }
    }
  }, [walkinGuestsCount, showBookingModal, user, resourcesList]);

  const loadProviderData = async () => {
    setLoading(true);
    setError('');

    // Safely get user and token — they might be null if session expired
    const currentToken = localStorage.getItem('token');
    const currentUser = (() => {
      try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
    })();

    if (!currentToken || !currentUser?._id) {
      setError("Session expirée. Veuillez vous reconnecter.");
      setLoading(false);
      return;
    }

    const authHeader = { Authorization: currentToken };
    let hasAnyError = false;

    // Refresh profile to get latest activity / resources
    try {
      const userRes = await axios.get(`/api/auth/OneUser/${currentUser._id}`);
      const updatedUser = userRes.data.OneUser || userRes.data;
      if (updatedUser) {
        setResourceName(updatedUser.resourceName || 'Table');
        setResourcesList(updatedUser.resources || []);
        setLogo(updatedUser.logo || '');
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (err) {
      console.error("❌ Erreur rafraîchissement profil:", err.message);
    }

    // 1. Get services (public, no auth needed)
    try {
      const servicesRes = await axios.get(`/api/services?provider=${currentUser._id}`);
      setServices(servicesRes.data);
    } catch (err) {
      console.error("❌ [1] Erreur chargement services:", err.response?.data || err.message);
      hasAnyError = true;
    }

    // 2. Get appointments
    try {
      const appointmentsRes = await axios.get('/api/appointments', { headers: authHeader });
      setAppointments(appointmentsRes.data);
    } catch (err) {
      console.error("❌ [2] Erreur chargement rendez-vous:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/Login';
        return;
      }
      hasAnyError = true;
    }

    // 3. Get promotions
    try {
      const promotionsRes = await axios.get('/api/promotions', { headers: authHeader });
      setPromotions(promotionsRes.data);
    } catch (err) {
      console.error("❌ [3] Erreur chargement promotions:", err.response?.data || err.message);
      setPromotions([]);
    }

    // 4. Get active chats
    try {
      const chatsRes = await axios.get('/api/messages/active', { headers: authHeader });
      setActiveChats(chatsRes.data);
    } catch (err) {
      console.error("❌ [4] Erreur chargement messages:", err.response?.data || err.message);
      setActiveChats([]);
    }

    // 5. Get current user profile to sync PINs
    try {
      const currentRes = await axios.get('/api/auth/CurrentUser', { headers: authHeader });
      if (currentRes.data) {
        setManagerPin(currentRes.data.managerPin || '1111');
        setCashiers(currentRes.data.cashiers || [{ name: 'Caissier Principal', pin: '2222' }]);
        setResourceName(currentRes.data.resourceName || 'Table');
        setResourcesList(currentRes.data.resources || []);
      }
    } catch (err) {
      console.error("❌ [5] Erreur chargement profil:", err.response?.data || err.message);
    }

    if (hasAnyError) {
      setError("Certaines données n'ont pas pu être chargées. Vérifiez votre connexion.");
    }

    setLoading(false);
  };


  const handleUpdateBoutique = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const res = await axios.put(`/api/auth/UpdateUser/${user._id}`, {
        businessName,
        description: businessDesc,
        adress: businessAddress,
        phone: parseInt(businessPhone) || undefined,
        managerPin,
        cashiers,
        resourceName,
        resources: resourcesList,
        logo
      }, {
        headers: { Authorization: token }
      });

      // Update local storage user details AND token
      const updatedUser = { ...user, ...(res.data.user || res.data) };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
      }
      setSuccess("Boutique et codes PIN mis à jour avec succès !");
    } catch (err) {
      console.error("Error updating boutique:", err);
      setError("Erreur lors de la mise à jour des paramètres.");
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.post('/api/services', {
        name: newServiceName,
        price: parseFloat(newServicePrice),
        duration: parseInt(newServiceDuration),
        description: newServiceDesc,
        activity: user.activity && typeof user.activity === 'object' ? user.activity._id : user.activity,
        maxCapacity: parseInt(newServiceMaxCapacity) || 1
      }, {
        headers: { Authorization: token }
      });

      setSuccess("Prestation ajoutée !");
      setShowServiceModal(false);
      
      // Reset
      setNewServiceName('');
      setNewServicePrice('');
      setNewServiceDesc('');
      setNewServiceMaxCapacity(1);
      
      const servicesRes = await axios.get(`/api/services?provider=${user._id}`);
      setServices(servicesRes.data);
    } catch (err) {
      setError("Erreur lors de l'ajout de la prestation.");
    }
  };

  const handleSetServicePromotion = async (e) => {
    e.preventDefault();
    if (!promoTargetService) return;
    try {
      const payload = promoServicePrice === ''
        ? { promotionPrice: null, discountLabel: '' }
        : { promotionPrice: parseFloat(promoServicePrice), discountLabel: promoServiceLabel };

      await axios.patch(`/api/services/${promoTargetService._id}/promotion`, payload, {
        headers: { Authorization: token }
      });

      setSuccess('Promotion mise à jour avec succès !');
      setShowPromoServiceModal(false);
      setPromoTargetService(null);
      setPromoServicePrice('');
      setPromoServiceLabel('');

      // Refresh services list
      const servicesRes = await axios.get(`/api/services?provider=${user._id}`);
      setServices(servicesRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour de la promotion.');
    }
  };

  const openEditServiceModal = (s) => {
    setEditTargetService(s);
    setEditServiceName(s.name || '');
    setEditServicePrice(s.price || '');
    setEditServiceDuration(s.duration || 30);
    setEditServiceDesc(s.description || '');
    setEditServiceMaxCapacity(s.maxCapacity || 1);
    setShowEditServiceModal(true);
  };

  const handleEditServiceSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.put(`/api/services/${editTargetService._id}`, {
        name: editServiceName,
        price: parseFloat(editServicePrice),
        duration: parseInt(editServiceDuration),
        description: editServiceDesc,
        maxCapacity: parseInt(editServiceMaxCapacity) || 1
      }, {
        headers: { Authorization: token }
      });

      setSuccess("Prestation modifiée avec succès !");
      setShowEditServiceModal(false);
      loadProviderData();
    } catch (err) {
      console.error("Error editing service:", err);
      setError(err.response?.data?.error || "Erreur lors de la modification de la prestation.");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette prestation ?")) return;
    setError('');
    setSuccess('');
    try {
      await axios.delete(`/api/services/${serviceId}`, {
        headers: { Authorization: token }
      });
      setSuccess("Prestation supprimée avec succès !");
      loadProviderData();
    } catch (err) {
      console.error("Error deleting service:", err);
      setError(err.response?.data?.error || "Erreur lors de la suppression de la prestation.");
    }
  };

  const handleAddPromo = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.post('/api/promotions', {
        code: promoCode,
        discountType: promoType,
        value: parseFloat(promoValue)
      }, {
        headers: { Authorization: token }
      });

      setSuccess("Code coupon créé avec succès !");
      setShowPromoModal(false);
      
      setPromoCode('');
      setPromoValue('');

      const promotionsRes = await axios.get('/api/promotions', {
        headers: { Authorization: token }
      });
      setPromotions(promotionsRes.data);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la création du coupon.");
    }
  };

  const handleWalkinSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!walkinClientName || !walkinServiceId || !walkinDate || !walkinTime) {
      setError("Veuillez remplir tous les champs obligatoires (*).");
      return;
    }

    try {
      const s = services.find(srv => srv._id === walkinServiceId);
      if (!s) {
        setError("Service introuvable.");
        return;
      }

      const providerActName = user?.activity && typeof user.activity === 'object'
        ? user.activity.name
        : user?.activity;
      const isResourceBased = user?.bookingMode === 'resources' ||
        providerActName?.toLowerCase().includes('restaurant') ||
        providerActName?.toLowerCase().includes('terrain');

      const bookingPayload = {
        clientName: walkinClientName,
        clientEmail: walkinClientEmail || `${walkinClientName.toLowerCase().replace(/\s+/g, '')}@passager.com`,
        clientPhone: walkinClientPhone || '00000000',
        serviceId: walkinServiceId,
        date: walkinDate,
        startTime: walkinTime,
        notes: walkinNotes,
        location: user.adress || '',
        forceBooking: true,
        guestsCount: isResourceBased ? walkinGuestsCount : 1,
        selectedResourceName: isResourceBased ? walkinSelectedResourceName : undefined
      };

      await axios.post('/api/appointments', bookingPayload, {
        headers: { Authorization: token }
      });

      setSuccess("✅ Rendez-vous sur place enregistré avec succès !");
      setShowBookingModal(false);
      
      setWalkinClientName('');
      setWalkinClientPhone('');
      setWalkinClientEmail('');
      setWalkinServiceId('');
      setWalkinDate('');
      setWalkinTime('09:00');
      setWalkinNotes('');
      setWalkinGuestsCount(1);
      setWalkinSelectedResourceName('');

      loadProviderData();
    } catch (err) {
      console.error("Error creating walkin booking:", err);
      setError(err.response?.data?.error || "Erreur lors de la création du rendez-vous.");
    }
  };

  const selectChatPartner = async (partner) => {
    setSelectedChatUser(partner);
    try {
      const res = await axios.get(`/api/messages/conversation/${partner._id}`, {
        headers: { Authorization: token }
      });
      setChatMessages(res.data);
    } catch (err) {
      console.error("Error loading chat messages:", err);
    }
  };

  const handleSendResponseChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !selectedChatUser) return;

    try {
      const res = await axios.post('/api/messages', {
        receiverId: selectedChatUser._id,
        text: chatInput
      }, {
        headers: { Authorization: token }
      });
      setChatMessages(prev => [...prev, res.data]);
      setChatInput('');
    } catch (err) {
      console.error("Error sending response message:", err);
    }
  };

  // Convert appointments to calendar events
  const calendarEvents = appointments.map(app => {
    try {
      const appDateStr = app.date.split('T')[0];
      return {
        id: app._id,
        title: `${app.clientName} - ${app.services?.map(s => s.name).join(', ') || app.service?.name || 'Prestation'}`,
        start: new Date(`${appDateStr}T${app.startTime}:00`),
        end: new Date(`${appDateStr}T${app.endTime || app.startTime}:00`),
        resource: app
      };
    } catch (e) {
      return null;
    }
  }).filter(Boolean);

  // Compute Statistics
  const totalRevenue = appointments
    .filter(a => a.status === 'confirmed' || a.status === 'completed')
    .reduce((sum, a) => sum + (a.totalPrice || 0), 0);

  const totalCommissions = appointments
    .filter(a => !a.isProviderCreated && (a.status === 'confirmed' || a.status === 'completed'))
    .reduce((sum, a) => sum + (a.commissionAmount || 0), 0);

  const netRevenue = totalRevenue - totalCommissions;

  const totalBookings = appointments.length;
  
  // Find popular services
  const serviceCounts = {};
  appointments.forEach(a => {
    const list = a.services || (a.service ? [a.service] : []);
    list.forEach(s => {
      serviceCounts[s.name] = (serviceCounts[s.name] || 0) + 1;
    });
  });
  const popularServices = Object.entries(serviceCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const eventStyleGetter = (event, start, end, isSelected) => {
    const isWalkin = event.resource?.isProviderCreated;
    const style = {
      backgroundColor: isWalkin ? 'var(--brand-gold)' : 'var(--brand-navy)',
      borderRadius: '8px',
      opacity: 0.95,
      color: isWalkin ? 'var(--brand-navy)' : 'white',
      border: 'none',
      display: 'block',
      fontWeight: '600',
      padding: '4px 8px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.06)',
      fontSize: '13px'
    };
    return { style };
  };

  const handleKeyPress = (num) => {
    if (enteredPin.length < 4) {
      setEnteredPin(prev => prev + num);
      setPinError('');
    }
  };

  const handleClear = () => {
    setEnteredPin('');
    setPinError('');
  };

  const handlePinSubmit = () => {
    const mPin = managerPin || "1111";

    if (enteredPin === mPin) {
      setSessionRole('manager');
      setSessionCashierName('Responsable');
      setEnteredPin('');
      setPinError('');
      return;
    }

    // Check cashiers list
    const matchedCashier = cashiers.find(c => c.pin === enteredPin);
    if (matchedCashier) {
      setSessionRole('cashier');
      setSessionCashierName(matchedCashier.name);
      setEnteredPin('');
      setPinError('');
    } else {
      setPinError('❌ Code PIN incorrect. Veuillez réessayer.');
      setEnteredPin('');
    }
  };

  if (!sessionRole) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100" style={{ background: 'var(--brand-navy-gradient)', color: 'white', fontFamily: "'Outfit', sans-serif", padding: '20px' }}>
        <Card className="border-0 shadow-lg p-4 text-center rounded-4" style={{ maxWidth: '400px', width: '100%', background: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
          <div className="fs-1 mb-2">🔒</div>
          <h3 className="font-weight-bold text-white mb-1">Espace Sécurisé</h3>
          <p className="text-white-50 small mb-4">Veuillez saisir votre code PIN d'accès</p>

          <div className="d-flex justify-content-center gap-3 mb-4">
            {[0, 1, 2, 3].map((idx) => (
              <div 
                key={idx}
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: enteredPin.length > idx ? 'var(--brand-gold)' : 'rgba(255, 255, 255, 0.2)',
                  boxShadow: enteredPin.length > idx ? '0 0 10px var(--brand-gold)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </div>

          {pinError && <div className="text-danger small mb-3 font-weight-bold">{pinError}</div>}

          {/* Keypad Grid */}
          <div className="d-grid gap-3" style={{ gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: '280px', margin: '0 auto' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <Button 
                key={num} 
                variant="dark" 
                onClick={() => handleKeyPress(num.toString())}
                className="py-3 fs-4 font-weight-bold rounded-circle border-0 d-flex align-items-center justify-content-center"
                style={{ width: '65px', height: '65px', background: 'rgba(255, 255, 255, 0.1)', color: 'white', transition: 'all 0.1s ease' }}
              >
                {num}
              </Button>
            ))}
            <Button 
              variant="danger" 
              onClick={handleClear}
              className="py-3 small font-weight-bold rounded-circle border-0 d-flex align-items-center justify-content-center"
              style={{ width: '65px', height: '65px', background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}
            >
              C
            </Button>
            <Button 
              variant="dark" 
              onClick={() => handleKeyPress('0')}
              className="py-3 fs-4 font-weight-bold rounded-circle border-0 d-flex align-items-center justify-content-center"
              style={{ width: '65px', height: '65px', background: 'rgba(255, 255, 255, 0.1)', color: 'white' }}
            >
              0
            </Button>
            <Button 
              variant="success" 
              onClick={handlePinSubmit}
              className="py-3 small font-weight-bold rounded-circle border-0 d-flex align-items-center justify-content-center"
              style={{ width: '65px', height: '65px', background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
              disabled={enteredPin.length < 4}
            >
              OK
            </Button>
          </div>

          <div className="mt-4 pt-3 border-top border-secondary text-white-50 small text-start">
            <div className="font-weight-bold mb-1">💡 Codes PIN actifs :</div>
            <div>• Responsable : <code>{managerPin}</code></div>
            {cashiers.map((c, idx) => (
              <div key={idx}>• {c.name} : <code>{c.pin}</code></div>
            ))}
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container style={{ marginTop: '100px', marginBottom: '40px' }}>
      <div className="p-4 mb-4 bg-white rounded-4 shadow-sm border-0 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
        <div>
          <h2 className="text-primary font-weight-bold mb-1">💼 Panel {user?.businessName || user?.name}</h2>
          <p className="text-muted mb-0">
            Session : <Badge bg="success" className="px-3 py-2 text-uppercase me-2">{sessionRole === 'manager' ? 'Responsable' : `Caissier : ${sessionCashierName}`}</Badge>
            Catégorie : <Badge bg="dark" className="px-3 py-2 text-uppercase">{user?.activity && typeof user.activity === 'object' ? user.activity.name : user?.activity || 'Services'}</Badge>
          </p>
        </div>
        <Button variant="outline-primary" onClick={loadProviderData} disabled={loading}>
          {loading ? 'Chargement...' : 'Actualiser'}
        </Button>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}
      {success && <div className="alert alert-success mb-4">{success}</div>}

      <Tab.Container id="provider-marketplace-tabs" defaultActiveKey={sessionRole === 'cashier' ? 'agenda' : 'stats'}>
        <Row className="mb-4">
          <Col>
            <Nav variant="pills" className="bg-white p-2 rounded-3 shadow-sm d-flex flex-wrap gap-2">
              {sessionRole === 'manager' && (
                <Nav.Item>
                  <Nav.Link eventKey="stats" className="px-3 py-2 font-weight-bold">📊 Statistiques</Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey="agenda" className="px-3 py-2 font-weight-bold">📅 Agenda</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="services" className="px-3 py-2 font-weight-bold">🛠️ Services</Nav.Link>
              </Nav.Item>
              {sessionRole === 'manager' && (
                <>
                  <Nav.Item>
                    <Nav.Link eventKey="promotions" className="px-3 py-2 font-weight-bold">🎁 Coupons</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="chat" className="px-3 py-2 font-weight-bold">💬 Messagerie</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="config" className="px-3 py-2 font-weight-bold">⚙️ Paramètres</Nav.Link>
                  </Nav.Item>
                </>
              )}
            </Nav>
          </Col>
        </Row>

        <Tab.Content>
          {/* Tab 1: Statistics Dashboard */}
          <Tab.Pane eventKey="stats">
            <Row className="g-4 mb-4">
              <Col md={3}>
                <Card className="border-0 shadow-sm rounded-4 p-4 text-center bg-white h-100">
                  <div className="fs-1 mb-2">💰</div>
                  <h6 className="text-muted text-uppercase mb-2 font-weight-bold">Revenu Brut</h6>
                  <h2 className="font-weight-bold text-success">{(totalRevenue || 0).toFixed(3)} DT</h2>
                  <small className="text-muted">Total des réservations</small>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm rounded-4 p-4 text-center bg-white h-100">
                  <div className="fs-1 mb-2">🛡️</div>
                  <h6 className="text-muted text-uppercase mb-2 font-weight-bold">Commissions</h6>
                  <h2 className="font-weight-bold text-danger">{(totalCommissions || 0).toFixed(3)} DT</h2>
                  <small className="text-muted">Part prélevée par Bookly (admin)</small>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm rounded-4 p-4 text-center bg-white h-100">
                  <div className="fs-1 mb-2">💵</div>
                  <h6 className="text-muted text-uppercase mb-2 font-weight-bold">Revenu Net</h6>
                  <h2 className="font-weight-bold text-primary">{(netRevenue || 0).toFixed(3)} DT</h2>
                  <small className="text-muted">Revenu réel après commissions</small>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm rounded-4 p-4 text-center bg-white h-100">
                  <div className="fs-1 mb-2">📅</div>
                  <h6 className="text-muted text-uppercase mb-2 font-weight-bold">Réservations</h6>
                  <h2 className="font-weight-bold text-dark">{totalBookings}</h2>
                  <small className="text-muted">Total des rendez-vous inscrits</small>
                </Card>
              </Col>
            </Row>

            {/* List of appointments and audit commissions */}
            <Card className="border-0 shadow-sm rounded-4 p-4 mt-4 bg-white">
              <h4 className="font-weight-bold text-secondary mb-3">Audit des Réservations & Commissions</h4>
              <div className="table-responsive">
                <Table hover striped bordered className="align-middle">
                  <thead className="bg-light">
                      <tr>
                        <th>Client</th>
                        <th>Date & Heure</th>
                        <th>Prestations</th>
                        <th>Prix Total</th>
                        <th>Origine</th>
                        <th>Ressource</th>
                        <th>Taux Commission</th>
                        <th>Montant Commission</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4 text-muted">Aucun rendez-vous enregistré.</td>
                        </tr>
                      ) : (
                        appointments.map((app) => {
                          const isWalkin = app.isProviderCreated;
                          const dateStr = app.date ? format(parseISO(app.date), 'dd MMMM yyyy', { locale: fr }) : '-';
                          return (
                            <tr key={app._id}>
                              <td>
                                <strong>{app.clientName}</strong>
                                {app.clientPhone && <div className="text-muted small">📞 {app.clientPhone}</div>}
                              </td>
                              <td>
                                <div>📅 {dateStr}</div>
                                <div className="text-muted small">⏰ {app.startTime} ({app.duration} min)</div>
                              </td>
                              <td>
                                {app.services?.map(s => s.name).join(', ') || app.service?.name || '-'}
                              </td>
                              <td className="font-weight-bold text-dark">
                                {(app.totalPrice || 0).toFixed(3)} DT
                              </td>
                              <td>
                                {isWalkin ? (
                                  <Badge bg="success" className="px-2 py-1">📍 Sur place</Badge>
                                ) : (
                                  <Badge bg="primary" className="px-2 py-1">🌐 Client en ligne</Badge>
                                )}
                              </td>
                              <td>
                                {app.bookedResource ? (
                                  <Badge bg="dark" className="px-2 py-1">
                                    🪑 {typeof app.bookedResource === 'object' ? app.bookedResource.name : app.bookedResource} ({app.guestsCount || 1} pers)
                                  </Badge>
                                ) : (
                                  <span className="text-muted">Standard</span>
                                )}
                              </td>
                              <td>
                                {isWalkin ? '0%' : `${app.commissionRate || 10}%`}
                              </td>
                              <td className={isWalkin ? 'text-muted' : 'text-danger font-weight-bold'}>
                                {isWalkin ? '0.000 DT' : `${(app.commissionAmount || 0).toFixed(3)} DT`}
                              </td>
                              <td>
                                <Badge bg={app.status === 'confirmed' ? 'success' : app.status === 'cancelled' ? 'danger' : 'secondary'}>
                                  {app.status}
                                </Badge>
                              </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card>
          </Tab.Pane>

          {/* Tab 2: Agenda Calendar */}
          <Tab.Pane eventKey="agenda">
            <Card className="shadow-sm border-0 rounded-4 p-4 bg-white">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="font-weight-bold text-secondary mb-0">Mon Agenda & Disponibilités</h4>
                <Button 
                  onClick={() => setShowBookingModal(true)} 
                  style={{ background: 'var(--brand-navy)', border: 'none', fontWeight: 'bold' }}
                >
                  🆕 Prendre RDV (Sur Place / Passager)
                </Button>
              </div>
              <div style={{ height: '600px' }}>
                <Calendar
                  localizer={localizer}
                  events={calendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  defaultView="week"
                  views={['month', 'week', 'day', 'agenda']}
                  style={{ height: '100%' }}
                  eventPropGetter={eventStyleGetter}
                />
              </div>
            </Card>
          </Tab.Pane>

          {/* Tab 3: Services Catalog */}
          <Tab.Pane eventKey="services">
            <Card className="shadow-sm border-0 rounded-4 p-4 bg-white">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="font-weight-bold text-secondary mb-0">Mes Services Boutique</h4>
                {sessionRole === 'manager' && (
                  <Button variant="primary" onClick={() => setShowServiceModal(true)}>
                    + Créer une Prestation
                  </Button>
                )}
              </div>
              {services.length === 0 ? (
                <div className="text-center py-5 text-muted bg-light rounded-3">Aucun service défini.</div>
              ) : (
                <Table hover bordered className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Service</th>
                      <th>Durée</th>
                      <th>Capacité</th>
                      <th>Prix Normal</th>
                      <th>Prix Promo</th>
                      {sessionRole === 'manager' && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {services.map(s => (
                      <tr key={s._id}>
                        <td>
                          <strong>{s.name}</strong>
                          {s.discountLabel && <Badge bg="danger" className="ms-2 small">{s.discountLabel}</Badge>}
                        </td>
                        <td>{s.duration} min</td>
                        <td><Badge bg="info">{s.maxCapacity || 1} client(s)</Badge></td>
                        <td className="text-muted">{s.price} DT</td>
                        <td>
                          {s.promotionPrice !== null && s.promotionPrice !== undefined ? (
                            <span className="text-danger fw-bold">{s.promotionPrice} DT</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        {sessionRole === 'manager' && (
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant={s.promotionPrice ? 'outline-danger' : 'outline-warning'}
                                className="rounded-pill px-3"
                                onClick={() => {
                                  setPromoTargetService(s);
                                  setPromoServicePrice(s.promotionPrice !== null && s.promotionPrice !== undefined ? s.promotionPrice : '');
                                  setPromoServiceLabel(s.discountLabel || '');
                                  setShowPromoServiceModal(true);
                                }}
                              >
                                {s.promotionPrice ? '✏️ Remise' : '🏷️ Remise'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="rounded-pill px-3"
                                onClick={() => openEditServiceModal(s)}
                              >
                                ✏️ Modifier
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="rounded-pill px-3"
                                onClick={() => handleDeleteService(s._id)}
                              >
                                🗑️ Supprimer
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </Tab.Pane>

          {/* Tab 4: Promotions & Coupons */}
          <Tab.Pane eventKey="promotions">
            <Card className="shadow-sm border-0 rounded-4 p-4 bg-white">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="font-weight-bold text-secondary mb-0">Coupons de réduction</h4>
                <Button variant="primary" onClick={() => setShowPromoModal(true)}>
                  + Créer un Coupon
                </Button>
              </div>
              {promotions.length === 0 ? (
                <div className="text-center py-5 text-muted bg-light rounded-3">Aucun code promo actif.</div>
              ) : (
                <Table hover striped bordered className="align-middle">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Type de remise</th>
                      <th>Remise</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotions.map(p => (
                      <tr key={p._id}>
                        <td><Badge bg="primary" className="text-uppercase px-3 py-2">{p.code}</Badge></td>
                        <td>{p.discountType === 'percentage' ? 'Pourcentage' : 'Montant Fixe'}</td>
                        <td><strong>{p.value} {p.discountType === 'percentage' ? '%' : 'DT'}</strong></td>
                        <td>
                          <Badge bg={p.active ? 'success' : 'secondary'}>{p.active ? 'Actif' : 'Désactivé'}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </Tab.Pane>

          {/* Tab 5: Messagerie Chat Client */}
          <Tab.Pane eventKey="chat">
            <Card className="shadow-sm border-0 rounded-4 overflow-hidden bg-white">
              <Row className="g-0" style={{ height: '500px' }}>
                {/* Left Side: Users List */}
                <Col md={4} className="border-end bg-light" style={{ overflowY: 'auto' }}>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="bg-light font-weight-bold text-secondary">
                      Conversations clients
                    </ListGroup.Item>
                    {activeChats.length === 0 ? (
                      <div className="text-center py-5 text-muted small">Aucune discussion active.</div>
                    ) : (
                      activeChats.map(partner => (
                        <ListGroup.Item 
                          key={partner._id}
                          action
                          onClick={() => selectChatPartner(partner)}
                          className={selectedChatUser?._id === partner._id ? 'bg-white font-weight-bold text-primary border-start border-primary border-4' : ''}
                        >
                          👤 {partner.name}
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </Col>

                {/* Right Side: Messages List */}
                <Col md={8} className="d-flex flex-column" style={{ height: '100%' }}>
                  {selectedChatUser ? (
                    <>
                      <div className="bg-light p-3 border-bottom font-weight-bold">
                        💬 Discuter avec {selectedChatUser.name}
                      </div>
                      <div className="flex-grow-1 p-3 overflow-y-auto" style={{ maxHeight: '380px' }}>
                        {chatMessages.map(m => {
                          const isMe = m.sender === user._id;
                          return (
                            <div key={m._id} className={`d-flex mb-2 ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                              <div className={`p-3 rounded-3 ${isMe ? 'bg-primary text-white' : 'bg-secondary-subtle text-dark'}`} style={{ maxWidth: '70%', fontSize: '14px' }}>
                                {m.text}
                                <div className="text-end small opacity-75 mt-1" style={{ fontSize: '10px' }}>
                                  {format(parseISO(m.createdAt), 'HH:mm')}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Form onSubmit={handleSendResponseChat} className="p-3 border-top d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="Saisissez votre message..."
                          value={chatInput}
                          onChange={e => setChatInput(e.target.value)}
                        />
                        <Button variant="primary" type="submit">Envoyer</Button>
                      </Form>
                    </>
                  ) : (
                    <div className="text-center my-auto text-muted">
                      Sélectionnez un client sur la gauche pour commencer à discuter.
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          </Tab.Pane>

          {/* Tab 6: Paramètres Boutique */}
          <Tab.Pane eventKey="config">
            <Card className="shadow-sm border-0 rounded-4 p-4 bg-white">
              <h4 className="font-weight-bold text-secondary mb-4">Configuration de ma Mini-Boutique</h4>
              <Form onSubmit={handleUpdateBoutique}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom Commercial de l'Établissement</Form.Label>
                  <Form.Control
                    type="text"
                    value={businessName}
                    onChange={e => setBusinessName(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description / Présentation de l'établissement</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={businessDesc}
                    onChange={e => setBusinessDesc(e.target.value)}
                    placeholder="Présentez votre spa, salle de sport, salon de coiffure..."
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Adresse physique complète</Form.Label>
                      <Form.Control
                        type="text"
                        value={businessAddress}
                        onChange={e => setBusinessAddress(e.target.value)}
                        placeholder="Ex: 45 Avenue Habib Bourguiba, Tunis"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Téléphone professionnel</Form.Label>
                      <Form.Control
                        type="tel"
                        value={businessPhone}
                        onChange={e => setBusinessPhone(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="align-items-center mb-3 mt-2">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Logo de l'Établissement (URL de l'image)</Form.Label>
                      <Form.Control
                        type="url"
                        value={logo}
                        onChange={e => setLogo(e.target.value)}
                        placeholder="Ex: https://lien-vers-mon-logo.png"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="text-center mt-3 mt-md-0">
                    {logo ? (
                      <div className="border rounded p-2 d-inline-block bg-light" style={{ maxWidth: '100%' }}>
                        <img 
                          src={logo} 
                          alt="Aperçu logo" 
                          style={{ height: '70px', objectFit: 'contain', maxWidth: '100%' }}
                          onError={(e) => { e.target.src = 'https://placehold.co/100x100?text=Logo+Invalide'; }}
                        />
                      </div>
                    ) : (
                      <div className="border rounded p-2 d-inline-block text-muted small bg-light" style={{ width: '100px', height: '80px', lineHeight: '60px' }}>
                        Aucun logo
                      </div>
                    )}
                  </Col>
                </Row>

                {user?.bookingMode === 'resources' && (
                  <Row className="mt-4 pt-3 border-top">
                    <h5 className="font-weight-bold text-primary mb-3">🪑 Configuration de mes {resourceName || 'Tables'}</h5>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nom de l'unité de ressource (singulier)</Form.Label>
                        <Form.Control
                          type="text"
                          value={resourceName}
                          onChange={e => setResourceName(e.target.value)}
                          placeholder="Ex: Table, Terrain, Bureau"
                          required
                        />
                      </Form.Group>
                      
                      <div className="p-3 border rounded-3 bg-light mb-3">
                        <h6 className="font-weight-bold text-dark mb-3">＋ Ajouter une nouvelle ressource</h6>
                        <Row className="g-3 align-items-end">
                          <Col md={6}>
                            <Form.Label className="small text-muted">Nom (Ex: Table A, Terrain 2)</Form.Label>
                            <Form.Control
                              type="text"
                              id="dashboard-res-name"
                              placeholder="Nom de la ressource"
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small text-muted">Capacité (personnes max)</Form.Label>
                            <Form.Control
                              type="number"
                              id="dashboard-res-capacity"
                              placeholder="Nb places"
                              min="1"
                              defaultValue="2"
                            />
                          </Col>
                          <Col md={2}>
                            <Button
                              variant="primary"
                              className="w-100"
                              onClick={() => {
                                const nameInput = document.getElementById('dashboard-res-name');
                                const capInput = document.getElementById('dashboard-res-capacity');
                                const name = nameInput.value.trim();
                                const cap = parseInt(capInput.value) || 2;

                                if (!name) {
                                  alert("Veuillez saisir un nom pour la ressource.");
                                  return;
                                }

                                setResourcesList(prev => [...prev, { name, capacity: cap }]);
                                nameInput.value = '';
                                capInput.value = '2';
                              }}
                            >
                              Ajouter
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <h6 className="font-weight-bold text-secondary mb-2">Ressources disponibles ({resourcesList.length}) :</h6>
                      {resourcesList.length === 0 ? (
                        <div className="text-muted small mb-3 text-center py-3 bg-white rounded border">Aucune ressource configurée pour le moment.</div>
                      ) : (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {resourcesList.map((r, idx) => (
                            <Badge key={idx} bg="light" className="text-dark border p-2 d-flex align-items-center gap-2">
                              <span>🪑 <strong>{r.name}</strong> ({r.capacity} pers. max)</span>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                style={{ padding: '0px 4px', fontSize: '10px', lineHeight: '1' }}
                                onClick={() => setResourcesList(prev => prev.filter((_, i) => i !== idx))}
                              >
                                ✕
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Col>
                  </Row>
                )}

                <Row className="mt-4 pt-3 border-top">
                  <h5 className="font-weight-bold text-primary mb-3">🔒 Codes PIN d'Accès Clavier</h5>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="font-weight-bold">PIN Responsable (Manager) - 4 chiffres</Form.Label>
                      <Form.Control
                        type="text"
                        maxLength="4"
                        pattern="\d{4}"
                        value={managerPin}
                        onChange={e => setManagerPin(e.target.value.replace(/\D/g, ''))}
                        placeholder="Ex: 1111"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3">
                  <h6 className="font-weight-bold text-secondary mb-3">👥 Liste des Caissiers Autorisés</h6>
                  {cashiers.length === 0 ? (
                    <div className="text-muted small mb-3 p-3 bg-light rounded-3 text-center">Aucun caissier configuré. Les caissiers ne pourront pas accéder à l'agenda.</div>
                  ) : (
                    <ListGroup className="mb-3">
                      {cashiers.map((c, idx) => (
                        <ListGroup.Item key={idx} className="d-flex justify-content-between align-items-center bg-light border-0 mb-2 rounded-3 p-3">
                          <div>
                            <strong>👤 {c.name}</strong> <span className="text-muted ms-3">Code PIN : </span><code>{c.pin}</code>
                          </div>
                          <Button 
                            variant="danger" 
                            size="sm" 
                            className="rounded-pill px-3"
                            onClick={() => setCashiers(prev => prev.filter((_, i) => i !== idx))}
                          >
                            Supprimer
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}

                  <div className="p-3 border rounded-3 bg-light mb-3">
                    <h6 className="font-weight-bold text-dark mb-3">＋ Ajouter un nouveau Caissier</h6>
                    <Row className="g-3">
                      <Col md={5}>
                        <Form.Control
                          type="text"
                          id="new-cashier-name"
                          placeholder="Nom du caissier (Ex: Sonia)"
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Control
                          type="text"
                          id="new-cashier-pin"
                          maxLength="4"
                          placeholder="PIN (4 chiffres)"
                        />
                      </Col>
                      <Col md={3}>
                        <Button 
                          variant="primary" 
                          className="w-100" 
                          onClick={() => {
                            const nameInput = document.getElementById('new-cashier-name');
                            const pinInput = document.getElementById('new-cashier-pin');
                            const name = nameInput.value.trim();
                            const pin = pinInput.value.trim().replace(/\D/g, '');

                            if (!name || pin.length !== 4) {
                              alert("Veuillez saisir un nom et un code PIN à 4 chiffres.");
                              return;
                            }

                            if (cashiers.some(c => c.pin === pin) || pin === managerPin) {
                              alert("Ce code PIN est déjà attribué. Veuillez en choisir un autre.");
                              return;
                            }

                            setCashiers(prev => [...prev, { name, pin }]);
                            nameInput.value = '';
                            pinInput.value = '';
                          }}
                        >
                          Ajouter
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </div>

                <Button variant="success" type="submit" className="px-4 mt-3">
                  Sauvegarder les modifications
                </Button>
              </Form>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Service Promotion Modal */}
      <Modal show={showPromoServiceModal} onHide={() => { setShowPromoServiceModal(false); setPromoTargetService(null); }} centered>
        <Modal.Header closeButton style={{ background: 'var(--brand-navy)' }}>
          <Modal.Title className="text-white">
            🏷️ Remise sur : <em>{promoTargetService?.name}</em>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSetServicePromotion}>
          <Modal.Body>
            <div className="p-3 bg-light rounded-3 mb-3">
              <div className="d-flex justify-content-between">
                <span className="text-muted small">Prix normal :</span>
                <strong>{promoTargetService?.price} DT</strong>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Prix promotionnel (DT)</Form.Label>
              <Form.Control
                type="number"
                step="0.001"
                min="0"
                max={promoTargetService ? promoTargetService.price - 0.001 : undefined}
                value={promoServicePrice}
                onChange={e => setPromoServicePrice(e.target.value)}
                placeholder="Laisser vide pour supprimer la remise"
              />
              <Form.Text className="text-muted">
                Laisser vide pour annuler la promotion existante.
              </Form.Text>
            </Form.Group>
            {promoServicePrice !== '' && parseFloat(promoServicePrice) > 0 && promoTargetService && (
              <div className="alert alert-warning py-2 small">
                💡 Réduction calculée : <strong>
                  -{Math.round((1 - parseFloat(promoServicePrice) / promoTargetService.price) * 100)}%
                </strong>
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Libellé de la remise (optionnel)</Form.Label>
              <Form.Control
                type="text"
                value={promoServiceLabel}
                onChange={e => setPromoServiceLabel(e.target.value)}
                placeholder="Ex: Offre Été, -20%, Promo Spéciale..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowPromoServiceModal(false); setPromoTargetService(null); }}>
              Annuler
            </Button>
            {promoTargetService?.promotionPrice && (
              <Button
                variant="outline-danger"
                onClick={() => {
                  setPromoServicePrice('');
                  setPromoServiceLabel('');
                }}
              >
                🗑️ Supprimer la remise
              </Button>
            )}
            <Button variant="success" type="submit">
              ✅ Appliquer
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Service Modal */}
      <Modal show={showServiceModal} onHide={() => setShowServiceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Créer un service</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddService}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom de la prestation</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Massage Thaïlandais"
                value={newServiceName}
                onChange={e => setNewServiceName(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tarif (DT)</Form.Label>
              <Form.Control
                type="number"
                placeholder="Tarif"
                value={newServicePrice}
                onChange={e => setNewServicePrice(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Durée</Form.Label>
              <Form.Select
                value={newServiceDuration}
                onChange={e => setNewServiceDuration(e.target.value)}
                required
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
                <option value={90}>90 min</option>
                <option value={120}>120 min</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (facultatif)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newServiceDesc}
                onChange={e => setNewServiceDesc(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Capacité maximale (réservations simultanées)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={newServiceMaxCapacity}
                onChange={e => setNewServiceMaxCapacity(parseInt(e.target.value) || 1)}
                required
              />
              <Form.Text className="text-muted">
                Nombre de clients pouvant réserver ce service en même temps sur le même créneau (Ex: 5 pour 5 fauteuils de pédicure).
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowServiceModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit">Créer</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Service Modal */}
      <Modal show={showEditServiceModal} onHide={() => setShowEditServiceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>✏️ Modifier la prestation</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditServiceSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom de la prestation</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Massage Thaïlandais"
                value={editServiceName}
                onChange={e => setEditServiceName(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tarif (DT)</Form.Label>
              <Form.Control
                type="number"
                placeholder="Tarif"
                value={editServicePrice}
                onChange={e => setEditServicePrice(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Durée</Form.Label>
              <Form.Select
                value={editServiceDuration}
                onChange={e => setEditServiceDuration(e.target.value)}
                required
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
                <option value={90}>90 min</option>
                <option value={120}>120 min</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (facultatif)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editServiceDesc}
                onChange={e => setEditServiceDesc(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Capacité maximale (réservations simultanées)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={editServiceMaxCapacity}
                onChange={e => setEditServiceMaxCapacity(parseInt(e.target.value) || 1)}
                required
              />
              <Form.Text className="text-muted">
                Nombre de clients pouvant réserver ce service en même temps sur le même créneau.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditServiceModal(false)}>Annuler</Button>
            <Button variant="success" type="submit">Enregistrer</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Promo Modal */}
      <Modal show={showPromoModal} onHide={() => setShowPromoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Créer un Coupon Code</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddPromo}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Code Coupon (Ex: WELCOME10)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: GAMER20"
                value={promoCode}
                onChange={e => setPromoCode(e.target.value)}
                required
                className="text-uppercase"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type de réduction</Form.Label>
              <Form.Select
                value={promoType}
                onChange={e => setPromoType(e.target.value)}
                required
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant Fixe (DT)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Valeur de la réduction</Form.Label>
              <Form.Control
                type="number"
                placeholder="Valeur de la remise"
                value={promoValue}
                onChange={e => setPromoValue(e.target.value)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPromoModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit">Créer le Coupon</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Walk-in Booking Modal */}
      <Modal show={showBookingModal} onHide={() => setShowBookingModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>🆕 Prendre un rendez-vous (Client Passager / Sur place)</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleWalkinSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom du Client *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nom complet"
                    value={walkinClientName}
                    onChange={e => setWalkinClientName(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Téléphone du Client (Facultatif)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Numéro de téléphone"
                    value={walkinClientPhone}
                    onChange={e => setWalkinClientPhone(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Email du Client (Facultatif)</Form.Label>
              <Form.Control
                type="email"
                placeholder="client@email.com"
                value={walkinClientEmail}
                onChange={e => setWalkinClientEmail(e.target.value)}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prestation *</Form.Label>
                  <Form.Select
                    value={walkinServiceId}
                    onChange={e => setWalkinServiceId(e.target.value)}
                    required
                  >
                    <option value="">-- Sélectionner un Service --</option>
                    {services.map(s => (
                      <option key={s._id} value={s._id}>{s.name} ({s.price} DT - {s.duration} min)</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={walkinDate}
                    onChange={e => setWalkinDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Heure *</Form.Label>
                  <Form.Select
                    value={walkinTime}
                    onChange={e => setWalkinTime(e.target.value)}
                    required
                  >
                    {Array.from({ length: 53 }).map((_, idx) => {
                      const h = Math.floor(idx * 15 / 60) + 8;
                      const m = (idx * 15) % 60;
                      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                      return <option key={timeStr} value={timeStr}>{timeStr}</option>;
                    })}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {(() => {
              const providerActName = user?.activity && typeof user.activity === 'object' 
                ? user.activity.name 
                : user?.activity;
              const isResourceBased = user?.bookingMode === 'resources' || 
                providerActName?.toLowerCase().includes('restaurant') || 
                providerActName?.toLowerCase().includes('terrain');
              const resourceLabel = resourceName || 
                (providerActName?.toLowerCase().includes('terrain') ? 'Terrain' : 'Table');
              const maxCap = resourcesList && resourcesList.length > 0 
                ? Math.max(...resourcesList.map(r => r.capacity), 10) 
                : 10;

              let providerResources = resourcesList || [];
              if (providerResources.length === 0) {
                if (providerActName?.toLowerCase().includes('terrain')) {
                  providerResources = [
                    { name: 'Court A', capacity: 10 },
                    { name: 'Court B', capacity: 10 },
                    { name: 'Court C', capacity: 10 }
                  ];
                } else {
                  providerResources = [
                    { name: 'Table 1', capacity: 2 },
                    { name: 'Table 2', capacity: 2 },
                    { name: 'Table 3', capacity: 4 },
                    { name: 'Table 4', capacity: 4 },
                    { name: 'Table 5', capacity: 6 }
                  ];
                }
              }

              const matchingResources = providerResources.filter(r => r.capacity >= walkinGuestsCount);

              if (!isResourceBased) return null;

              return (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Nombre de personnes *</Form.Label>
                      <Form.Select 
                        value={walkinGuestsCount} 
                        onChange={e => setWalkinGuestsCount(parseInt(e.target.value))}
                      >
                        {Array.from({ length: maxCap }, (_, i) => i + 1).map(num => (
                          <option key={num} value={num}>
                            {num} personne{num > 1 ? 's' : ''}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Choix de la {resourceLabel} / Espace *</Form.Label>
                      <Form.Select 
                        value={walkinSelectedResourceName} 
                        onChange={e => setWalkinSelectedResourceName(e.target.value)}
                        required
                      >
                        <option value="">-- Choisir un(e) {resourceLabel.toLowerCase()} --</option>
                        {matchingResources.map((r, i) => (
                          <option key={i} value={r.name}>
                            {r.name} (Capacité max: {r.capacity} pers)
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              );
            })()}

            <Form.Group className="mb-3">
              <Form.Label>Notes & Remarques (Facultatif)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Détails additionnels du rendez-vous..."
                value={walkinNotes}
                onChange={e => setWalkinNotes(e.target.value)}
              />
            </Form.Group>

            <div className="alert alert-info py-2 small mb-0">
              💡 <strong>Rappel :</strong> Les réservations prises sur place par le prestataire lui-même ne sont pas facturées (sans commission).
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBookingModal(false)}>Annuler</Button>
            <Button 
              variant="success" 
              type="submit" 
              style={{ backgroundColor: 'var(--brand-gold)', borderColor: 'var(--brand-gold)', color: 'var(--brand-navy)', fontWeight: 'bold' }}
            >
              Enregistrer le RDV
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ProviderDashboard;
